export enum Progress {
  Initial = 'initial',
  InProgress = 'in progress',
  Invalid = 'invalid',
  Valid = 'Valid',
  Complete = 'complete'
}

export type AnswerCountState = {
  [key: string]: number;
};

export type Image = {
  alt?: string;
  src: string;
};

export type Answer = {
  text?: string;
  image: Image;
  attribution?: string;
  tidbit?: string;
  subject: string;
};

export type Question = {
  question: string;
};

export type Subjects = string[];

export type QASet = {
  question: string;
  tip?: string;
  weight: number;
  answers: Answer[];
};

export type Hab = {
  email?: string;
  jobTitle?: string;
  location?: string;
  link?: string;
  name: string;
  image: Image;
};

export type TarotCard = {
  title: string;
  description: string;
  slug: string;
  image: Image;
  socialMediaImage: Image;
  socialMediaTitle: string;
  socialMediaDescription: string;
  habsHeading?: string;
  habs?: Hab[];
};

export type Data = {
  page: {
    title: string;
    description: string;
    image: Image;
  };
  dictionary: {
    footer_privacy_disclaimer: string;
    form_error_button_text: string;
    form_error_text: string;
    h1: string;
    introduction_text: string;
    next: string;
    newsletter_description: string;
    newsletter_heading: string;
    newsletter_label_name: string;
    newsletter_label_email: string;
    newsletter_signup: string;
    other_results: string;
    other_results_link_text: string;
    other_results_url: string;
    page_title: string;
    previous: string;
    privacy_policy_url: string;
    progress: string;
    progress_complete: string;
    pull_tarot_card: string;
    retake: string;
    share: string;
    take_the_quiz: string;
    terms_of_use_url: string;
    tip: string;
  };
  questions: QASet[];
  cards: TarotCard[];
};

export const data: Data = {
  page: {
    title: 'Say farewell to 2020 with Habanero’s new year tarot quiz.',
    description: 'Tell us what you did this year and we’ll guess what’s in store for you in 2021.',
    image: {
      src: '/2020/images/social/default.png'
    }
  },
  dictionary: {
    pull_tarot_card: 'Pull my tarot card',
    form_error_button_text: 'Answer them',
    form_error_text: 'Looks like you have some questions to finish before we pull your card.',
    h1: 'Habanero presents "Farewell 2020"',
    introduction_text:
      'Tell us what kept you going in 2020, and we’ll pull a super-magical tarot card with all the insight you need to make the most of 2021.',
    next: 'Go to the previous question',
    newsletter_description: 'We send one or two emails per month with articles, case studies, and Habanero news and events for you to stay in the loop.',
    newsletter_heading: 'Sign up for more future-ready insights.',
    newsletter_label_name: 'First and last name',
    newsletter_label_email: 'Email',
    newsletter_signup: 'Sign me up',
    other_results: `Interested in seeing the other results you could have received?`,
    other_results_link_text: 'Check them out here!',
    other_results_url: 'https://www.habaneroconsulting.com/stories/insights/2020/holiday-tarot-cards',
    page_title: 'Habby Holidays!',
    previous: 'Go to the previous question',
    footer_privacy_disclaimer: 'Copyright ©2020 Habanero Consulting Group. All rights reserved. Please read our terms of use and privacy policy.',
    privacy_policy_url: 'https://www.habaneroconsulting.com/privacy-policy',
    progress: `Question {count} of 7`,
    progress_complete: 'You made it!',
    retake: 'Retake the quiz',
    share: 'Share your results with your network',
    take_the_quiz: 'Take the quiz!',
    terms_of_use_url: 'https://www.habaneroconsulting.com/terms-of-use',
    tip: '<span style="font-weight: 600">Pro tip:</span> Click the arrow to expand your answer and find out which Hab shares your interests.'
  },
  questions: [
    {
      question: 'What did you watch?',
      weight: 4,
      answers: [
        {
          text: 'TED Talks',
          image: {
            alt: 'A person standing on a stage in front of a large red TED sign, gesturing to an audience.',
            src: '/2020/images/cards/watch/ted-talk.jpg'
          },
          attribution: 'Photo courtesy of TED',
          tidbit: 'Director of Employee Experience James’s must-watch Ted Talk is David Kelley’s “How to build your creative confidence.”',
          subject: 'insight'
        },
        {
          text: 'Tiger King',
          image: {
            alt: 'Joe Exotic seated next to a tiger.',
            src: '/2020/images/cards/watch/tiger-king.jpg'
          },
          attribution: 'Photo courtesy of Netflix',
          tidbit:
            'Digital Workplace Consultant Mustansir doesn’t binge watch anything, except for Tiger King! Once he started, he couldn’t stop.',
          subject: 'adventure'
        },
        {
          text: 'The Bachelorette',
          image: {
            alt: 'A young woman in a sequin dress smiling and posing for a photo surrounded by three rows of men dressed in formal suits and tuxedos.',
            src: '/2020/images/cards/watch/bachelorette.jpg'
          },
          attribution: 'Photo courtesy of ABC',
          tidbit: 'UX Consultant Craig designed a website for our annual Bachelor office pool!',
          subject: 'togetherness'
        },
        {
          text: 'Selling Sunset',
          image: {
            alt: 'Three blonde-haired women dressed in red, standing in front of a luxury house and palm trees. The woman in front is holding out a set of gold house keys towards the viewer.',
            src: '/2020/images/cards/watch/selling-sunset.jpg'
          },
          attribution: 'Photo courtesy of Netflix',
          tidbit: 'Interaction Designer Michelle loves to “hate-watch” Selling Sunset!',
          subject: 'ambition'
        },
        {
          text: 'Great British Bake Off',
          image: {
            alt: 'The hosts and judges of the Great British Bake Off – Matt Lucas, Noel Fielding, Prue Leith and Paul Hollywood – holding trays of different cakes outside the baking tent.',
            src: '/2020/images/cards/watch/baking-show.jpg'
          },
          attribution: 'Photo courtesy of Channel 4',
          tidbit: 'Office Manager Jarrod is a big fan of the Great British Bake Off!',
          subject: 'inspiration'
        },
        {
          text: 'Queer Eye',
          image: {
            alt: 'The Queer Eye fab five – Antoni Porowski, Tan France, Karamo Brown, Bobby Berk and Jonathan Van Ness – dressed in suits and standing together and playfully posing.',
            src: '/2020/images/cards/watch/queer-eye.jpg'
          },
          attribution: 'Photo courtesy of Netflix',
          tidbit: 'Digital Workplace Consultant Scott’s favourite episode is Queer Eye Japan!',
          subject: 'mindfulness'
        }
      ]
    },
    {
      question: 'I passed the time by...',
      weight: 1,
      answers: [
        {
          text: 'Reading',
          image: {
            alt: 'A woman holding an open book in front of her face.',
            src: '/2020/images/cards/pass-time/reading.jpg'
          },
          tidbit: 'Interaction Designer Danielle loves to read her 2020 fav, Creative Confidence!',
          subject: 'insight'
        },
        {
          text: 'Getting outdoors',
          image: {
            alt: 'A woman wearing a backpack. Behind her is a scenic backdrop of the Rocky Mountains, pine trees and a green valley.',
            src: '/2020/images/cards/pass-time/hiking.jpg'
          },
          tidbit: 'Experience Designer Megan enjoys hiking Ribbon Falls.',
          subject: 'adventure'
        },
        {
          text: 'Video chatting with friends and family',
          image: {
            alt: 'A man sitting with his toddler son on his lap, talking and laughing with coworkers over video chat.',
            src: '/2020/images/cards/pass-time/video-chatting.jpg'
          },
          tidbit: 'We love staying connected to one another with Microsoft Teams!',
          subject: 'togetherness'
        },
        {
          text: 'Learning a new skill',
          image: {
            alt: 'A woman painting a scene at her easel while sitting near a bright window.',
            src: '/2020/images/cards/pass-time/new-skill.jpg'
          },
          tidbit: 'Interaction Designer Chrissy loves to paint in her spare time!',
          subject: 'ambition'
        },
        {
          text: 'Working on DIY projects',
          image: {
            alt: 'A bearded man with glasses is smiling as he uses a hammer to hang a picture on the wall.',
            src: '/2020/images/cards/pass-time/diy.jpg'
          },
          tidbit: 'Creative Director Kurtis’s favourite DIY of the year is his greenhouse!',
          subject: 'inspiration'
        },
        {
          text: 'Meditating',
          image: {
            alt: 'A man sitting cross-legged on his couch with his eyes closed, next to a sleeping puppy.',
            src: '/2020/images/cards/pass-time/meditating.jpg'
          },
          tidbit: 'QA Consultant Madhava helps us stay zen with weekly meditation sessions.',
          subject: 'mindfulness'
        }
      ]
    },
    {
      question: 'What was your go-to outfit?',
      weight: 6,
      answers: [
        {
          text: 'Something that makes me feel good',
          image: {
            alt: 'A man, smiling and wearing a flamingo shirt, standing next to a decorated Christmas tree.',
            src: '/2020/images/cards/outfit/feel-good.jpg'
          },
          tidbit: 'Digital Workplace Consultant Fraser looks and feels good in his flamingo shirt. ',
          subject: 'insight'
        },
        {
          text: 'Business on the top, sweatpants on the bottom',
          image: {
            alt: 'A man with a serious expression standing with his arms folded. He is wearing a headset and dress shirt with sweatpants.',
            src: '/2020/images/cards/outfit/business-on-top.jpg'
          },
          tidbit: 'Digital Workplace Advisor Imran looks good in his “video chat mullet” attire!',
          subject: 'adventure'
        },
        {
          text: 'A matching sweater with my dog',
          image: {
            alt: 'A woman wearing a red and black plaid coat with a small white poodle, who is wearing the same coat, on her lap. They are sitting in a park, with yellow leaves on the ground and trees.',
            src: '/2020/images/cards/outfit/matching-sweaters.jpg'
          },
          tidbit: 'Interaction Designer Carmen and her dog Lexi have the same coat. Who wore it best?',
          subject: 'togetherness'
        },
        {
          text: 'Something waterproof',
          image: {
            alt: 'A woman wearing a raincoat with the hood up. She is standing in a park, among tall pine trees. She is smiling and gesturing with two thumbs up.',
            src: '/2020/images/cards/outfit/something-waterproof.jpg'
          },
          tidbit: 'Experience Designer Laura sports her waterproof wear, essential for rainy Vancouver!',
          subject: 'ambition'
        },
        {
          text: 'Tie dye',
          image: {
            alt: 'A woman wearing a rainbow-coloured tie-dyed shirt standing at a stand-up desk. Her body is facing the desk, which has a computer monitor, keyboard, mouse, laptop on a stand and a mug on it, but she is turning her face to look and smile at the viewer.',
            src: '/2020/images/cards/outfit/tie-dye.jpg'
          },
          tidbit: 'Employee Experience Coordinator Ashleigh bought her tie-dye t-shirt from a local Toronto artist!',
          subject: 'inspiration'
        },
        {
          text: 'Athleisure',
          image: {
            alt: 'A woman casually leaning on her standing desk while wearing a hooded sweatshirt and athletic pants.',
            src: '/2020/images/cards/outfit/athleisure.jpg'
          },
          tidbit: 'Senior Advisor of Workplace Experience Cat has been sporting athleisure-wear this year!',
          subject: 'mindfulness'
        }
      ]
    },
    {
      question: 'What did you buy?',
      weight: 2,
      answers: [
        {
          text: 'Books',
          image: {
            alt: 'A selection of books arranged in rows: The Five Dysfunctions of a Team by Patrick Lencioni; The Corporate Survival Guide by Edgar Schein; Getting Naked by Patrick Lencioni; First, Break All the Rules by Marcus Buckingham and Curt Coffman; Strengths Finder 2.0 by Tim Rath; Value Proposition Design from Strategyzer; Built to Last by Jim Collins; and The Connected Company by David Gray.',
            src: '/2020/images/cards/buy/books.jpg'
          },
          tidbit: 'Content Consultant Sandra’s 2020 book club pick is New Self, New World',
          subject: 'insight'
        },
        {
          text: 'A bike',
          image: {
            alt: 'A man standing astride a road bike outside an office building. He is wearing lycra biking shorts and jersey, clip-on shoes and a backpack. His bike helmet is hanging off the handlebars.',
            src: '/2020/images/cards/buy/bike.jpg'
          },
          tidbit: 'President Steven is an avid mountain biker!',
          subject: 'adventure'
        },
        {
          text: 'Skincare',
          image: {
            alt: 'A smiling woman wearing a purple bathrobe posing with bottles of skincare products. Her hair is clipped back and she has a masque on her face.',
            src: '/2020/images/cards/buy/skincare.jpg'
          },
          tidbit: 'Digital Marketing Coordinator Sarah is our resident skincare guru!',
          subject: 'togetherness'
        },
        {
          text: 'Home workout equipment',
          image: {
            alt: 'A yoga mat, foam roller, six free weights and a large ball arranged in front of a large tv set on a stand.',
            src: '/2020/images/cards/buy/workout-equipment.jpg'
          },
          tidbit: 'Solution Developer Kelsey has an impressive home workout setup!',
          subject: 'ambition'
        },
        {
          text: 'Puzzles',
          image: {
            alt: 'A woman and a young child sitting on a furry pink rug working on a puzzle. The woman is leaning over to add a piece to the corner of the puzzle while the young child watches excitedly. They are surrounded by stuffies, including a big unicorn, a leopard and a little pig.',
            src: '/2020/images/cards/buy/puzzles.jpg'
          },
          tidbit: 'Project manager Toni and her daughter Charlie love doing puzzles together!',
          subject: 'inspiration'
        },
        {
          text: 'Plants',
          image: {
            alt: 'Lush green plants, including a fern, pothos and snake plant arranged in front of a painting of pine trees and the northern lights.',
            src: '/2020/images/cards/buy/plants.jpg'
          },
          tidbit: 'Can you spot Digital Workplace Consultant Julie’s kitten Bubba alongside her plants?',
          subject: 'mindfulness'
        }
      ]
    },
    {
      question: 'What did you listen to?',
      weight: 3,
      answers: [
        {
          text: 'Podcasts',
          image: {
            alt: `Closeup view of a person's hand selecting a podcast on their phone while browsing on their laptop.`,
            src: '/2020/images/cards/listen/podcasts.jpg'
          },
          tidbit: 'Director of Content Marketing Pilar’s go-to podcast is “Ten Percent Happier”!',
          subject: 'insight'
        },
        {
          text: 'Danger zone',
          image: {
            alt: 'Poster for the movie Top Gun: Maverick. Tom Cruise is leaning against the side of a World War 2-era prop plane, just behind the cockpit. He is wearing a white t-shirt and jeans. Behind him, the propellers of the plane look dark against a blue sky with a few light clouds.',
            src: '/2020/images/cards/listen/danger-zone.jpg'
          },
          attribution: 'Photo courtesy of Paramount Pictures',
          tidbit: 'Digital Workplace Consultant Mike D loves Top Gun so much he named his first car Goose!',
          subject: 'adventure'
        },
        {
          text: 'Harry Styles',
          image: {
            alt: 'Harry Styles wearing high-waisted sailor pants in flowing white fabric, with two rows of gold buttons in the front and a silky fuscia dress shirt, unbuttoned to his chest, with pink suspenders. He is standing agains a light blue backdrop with his hips thrust forward. One hand is on his waist; the other hand is pointing to the right. His hair is mussed and is mouth is open as if he is speaking.',
            src: '/2020/images/cards/listen/harry-styles.jpg'
          },
          attribution: 'Photo Courtesy of Columbia Records',
          tidbit: 'Danielle, Carmen and Nataliya like to listen to “Watermelon Sugar”!',
          subject: 'togetherness'
        },
        {
          text: 'Cardi B',
          image: {
            alt: 'Cardi B sitting in a yellow chair. She’s wearing a bold black-and-white checked dress, bright yellow short-haired wig, dark lipstick and white-framed, cat-eye sunglasses. He shadow looms large behind her against a turquoise background.',
            src: '/2020/images/cards/listen/cardi.jpg'
          },
          attribution: 'Photo courtesy of Atlantic Records',
          tidbit: 'Digital Workplace Consultant Elise puts on Cardi B when she’s getting pumped up to do chores!',
          subject: 'ambition'
        },
        {
          text: 'The Hamilton Soundtrack',
          image: {
            alt: 'Poster for the broadway musical Hamilton. A black five-point star sits against an ochre yellow background. The top point of the star is missing. Instead, Hamilton stands in the shape of the top point, with his legs in a wide stance and one arm raised in the air. White text across the star reads: “Hamilton: An American Musical.” ',
            src: '/2020/images/cards/listen/hamilton.jpg'
          },
          attribution: 'Photo courtesy of Atlantic Records',
          tidbit: 'Technical Specialist Ruben had Hamilton tickets…before 2020 happened.',
          subject: 'inspiration'
        },
        {
          text: 'Dreams by Fleetwood Mac',
          image: {
            alt: 'Screencap from Tik Tok star Nathan Apodaca’s viral video. He is tilting his head back to drink cranberry juice from the bottle while skateboarding and lipsynching Dreams by Fleetwood Mac.',
            src: '/2020/images/cards/listen/fleetwood-mac.jpg'
          },
          attribution: 'Photo courtesy of TikTok',
          tidbit: 'GO Product Manager Christina loves to listen to her Fleetwood Mac records with a glass of wine!',
          subject: 'mindfulness'
        }
      ]
    },
    {
      question: 'Pick your quarantine buddy.',
      weight: 6,
      answers: [
        {
          text: 'A cat',
          image: {
            alt: 'An orange tabby cat wearing a bandana peeking around a corner.',
            src: '/2020/images/cards/buddy/cat.jpg'
          },
          tidbit: 'This is Product Designer Jorge’s cat, Otto!',
          subject: 'insight'
        },
        {
          text: 'A black lab',
          image: {
            alt: 'A black Labrador dog sitting on a log in the woods.',
            src: '/2020/images/cards/buddy/lab.jpg'
          },
          tidbit: 'This is Senior Consultant Barb’s black lab, Maggie!',
          subject: 'adventure'
        },
        {
          text: 'A lap dog',
          image: {
            alt: 'A sweet-looking chihuahua sleeping on a sheepskin rug in a sunbeam.',
            src: '/2020/images/cards/buddy/connie.jpg'
          },
          tidbit: 'This is Front-End Developer Nataliya’s chihuahua, Connie!',
          subject: 'togetherness'
        },
        {
          text: 'Alexa',
          image: {
            alt: 'An Alexa device, resembling a cylindrical speaker, sitting on a side table next to a lamp and a plant.',
            src: '/2020/images/cards/buddy/alexa.jpg'
          },
          tidbit: 'Front-End Developer Jasjit likes to use his Echo to control and automate his devices – like lights and heat!',
          subject: 'ambition'
        },
        {
          text: 'An American bulldog',
          image: {
            alt: 'A white bulldog with black patches, including over one eye and ear, staring intensely at the viewer.',
            src: '/2020/images/cards/buddy/bulldog.jpg'
          },
          tidbit: 'This is Director of Product and Service Brian’s new dog Tucker!',
          subject: 'inspiration'
        },
        {
          text: 'A rescue',
          image: {
            alt: 'A happy-looking tan dog sitting in the grass on a sunny day. He is panting and there is a big stick laying near his front paws.',
            src: '/2020/images/cards/buddy/rescue.jpg'
          },
          tidbit: 'This is Griffin, a rescue dog who belongs to Senior Solution Developer Michael (aka Marms)!',
          subject: 'mindfulness'
        }
      ]
    },
    {
      question: 'Pick a meme you identify with most.',
      weight: 7,
      answers: [
        {
          image: {
            alt: 'A screen cap of The Real Housewives of Beverly Hills cast members Taylor Armstrong and Kyle Richards pointing and screaming in anger, followed by a picture of a confused-looking white cat sitting in a chair in front of a plate of vegetables.',
            src: '/2020/images/cards/meme/yelling-cat.jpg'
          },
          subject: 'insight'
        },
        {
          image: {
            alt: 'A Tweet with a grainy security cam image of Joe Exotic from Tiger King in prison, and the caption, "What my coworkers see in a conference call three weeks into quarantine when I’ve cut my own hair, hashtag Tiger King, hash tag Coronavirus.”',
            src: '/2020/images/cards/meme/conference-call.jpg'
          },
          subject: 'adventure'
        },
        {
          image: {
            alt: 'Kylo Ren teaching Baby Yoda to say “vegetables” one syllable at a time over four panels. In the final panel, Kylo Ren says, “Okay, say vegetables!” and Baby Yoda replies, “Chiccy nuggies.”',
            src: '/2020/images/cards/meme/baby-yoda.jpg'
          },
          subject: 'togetherness'
        },
        {
          image: {
            alt: 'Four characters from the movie Bridesdmaids walking together and smiling, with the label “My plans,” followed by a close-up of Melissa McCarthy’s face, red-faced and screaming, with the label “2020.”',
            src: '/2020/images/cards/meme/bridesmaids.jpg'
          },
          subject: 'ambition'
        },
        {
          image: {
            alt: 'A panel where Joey from the tv show Friends sits in a chair, relaxed and smiling, with the caption, “laughing at people fighting over toilet paper,” followed a second panel where he widens his eyes in shock with the caption, “realizing you’re out of toilet paper.”',
            src: '/2020/images/cards/meme/friends-toilet-paper.jpg'
          },
          subject: 'inspiration'
        },
        {
          image: {
            alt: 'A two-panel cartoon of a dog telling himself “This is fine,” while he sits at a table in a room engulfed in flames and smoke.',
            src: '/2020/images/cards/meme/this-is-fine.jpg'
          },
          subject: 'mindfulness'
        }
      ]
    }
  ],
  cards: [
    {
      title: 'Insight',
      description:
        'You’re a perpetual learner. There’s nothing you love more than to curl up with a good book, your favourite podcast or a playlist of video tutorials. This year, you discovered new writers and thinkers that kept you grounded and inspired through challenging times. You’re able to find connections and weave ideas together in meaningful ways. In 2021, you can put that wisdom to good use in your community to help design innovative solutions to long-standing problems.',
      slug: 'insight',
      image: {
        alt: `A drawing of a young person with black hair and a fair complexion set against a green background. They’re wearing round glasses and reading a blue book. They look calm and happy. On either side of them are two stacks of blue, pink and grey books.
        Five open books seem to dance above their head. At the bottom, white text on a black background says, “The Seeker.”`,
        src: '/2020/images/results/seeker.png'
      },
      socialMediaImage: {
        src: '/2020/images/social/the-seeker.png'
      },
      socialMediaTitle: 'Say farewell to 2020 with Habanero’s holiday tarot quiz.',
      socialMediaDescription: 'Find out how to make the most of the new year.',
      habsHeading: 'Check out the other Habs who will be having an insightful 2021:',
      habs: [
        {
          email: 'alaw@habaneroconsulting.com',
          jobTitle: 'Director, Finance and Performance',
          location: 'Vancouver',
          name: 'Aaron Law',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/aaron-law.jpg?la=en\u0026mw=213\u0026modified=20170823163825\u0026hash=BE22DE0595A498F409F2DCBFDBBEE302795D073A'
          }
        },
        {
          email: 'brichards@habaneroconsulting.com',
          jobTitle: 'Senior Consultant',
          location: 'Vancouver',
          name: 'Barbara Richards',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/barbara-richards.jpg?la=en\u0026mw=213\u0026modified=20191119125503\u0026hash=003FA72D4B7682A8F22BCCB823B8142BD7942C20'
          }
        },
        {
          email: 'balves@habaneroconsulting.com',
          jobTitle: 'Experience Consultant',
          location: 'Toronto',
          name: 'Bruno Alves',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/bruno-alves.jpg?la=en\u0026mw=213\u0026modified=20180420162810\u0026hash=B23E40CE8B20574B4A2C64B0183966C6A3682F4E'
          }
        },
        {
          email: 'ccraciun@habaneroconsulting.com',
          jobTitle: 'Infrastructure Architect',
          location: 'Vancouver',
          name: 'Carmen Craciun',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/carmen-craciun.jpg?la=en\u0026mw=213\u0026modified=20170822212140\u0026hash=C9A2CDDF6B96BC36EBCD4062576815AE3F1F700C'
          }
        },
        {
          email: 'cradcliffe@habaneroconsulting.com',
          jobTitle: 'Digital Workplace Advisor',
          location: 'Winnipeg',
          name: 'Chris Radcliffe',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/chris-radcliffe.jpg?la=en\u0026mw=213\u0026modified=20170822212102\u0026hash=325CDAFE8FD0C7117CFBF930FD79A553C5D89A01'
          }
        },
        {
          email: 'csweeney@habaneroconsulting.com',
          jobTitle: 'Quality Assurance Specialist',
          location: 'Vancouver',
          name: 'Chris Sweeney',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/chris-sweeney.jpg?la=en\u0026mw=213\u0026modified=20170822221109\u0026hash=5B186646888454EA843A2EBA7842EC06D3BB38F5'
          }
        },
        {
          email: 'cferancik@habaneroconsulting.com',
          jobTitle: 'Product Manager',
          location: 'Vancouver',
          name: 'Christina Ferancik',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/christina-ferancik.jpg?la=en\u0026mw=213\u0026modified=20190523161654\u0026hash=97B2AA2EED93048BB5BAFAEFB9A7501C1CDF7AAE'
          }
        },
        {
          email: 'cparsons@habaneroconsulting.com',
          jobTitle: 'Front-End Architect',
          location: 'Toronto',
          name: 'Christopher Parsons',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/chris-parsons.jpg?la=en\u0026mw=213\u0026modified=20170822214209\u0026hash=3F2F135CD427814581E7F8666C780EC4C3B6B511'
          }
        },
        {
          email: 'ckeller@habaneroconsulting.com',
          jobTitle: 'Front-End Developer',
          location: 'Calgary',
          name: 'Corey Keller',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/corey-keller.jpg?la=en\u0026mw=213\u0026modified=20170822212513\u0026hash=783BD86D6F81332E86C9EFB79A94310E83A38D0F'
          }
        },
        {
          email: 'csmith@habaneroconsulting.com',
          jobTitle: 'UX Consultant',
          location: 'Calgary',
          name: 'Craig Smith',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/craig-smith.jpg?la=en\u0026mw=213\u0026modified=20170822212453\u0026hash=3E5BFF5DD9C5BB3C9E3939A7FB475E7C4CBE57E5'
          }
        },
        {
          email: 'dkovacic@habaneroconsulting.com',
          jobTitle: 'Interaction Designer',
          location: 'Calgary',
          name: 'Danielle Kovacic',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/danielle-kovacic.jpg?la=en\u0026mw=213\u0026modified=20180928222756\u0026hash=B904F6A888F791BDBAEC442BD96A2CB6DFC5C495'
          }
        },
        {
          email: 'dwebb@habaneroconsulting.com',
          jobTitle: 'Technical Architect',
          location: 'Vancouver',
          name: 'David Webb',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/david-webb.jpg?la=en\u0026mw=213\u0026modified=20170822212435\u0026hash=15D1D466FB26BAB68CF7A23CD94969D311CB66CB'
          }
        },
        {
          email: 'ddesouza@habaneroconsulting.com',
          jobTitle: 'AvePoint Lead, Western Canada',
          location: 'Calgary',
          name: 'Davis de Souza',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/davis-de-souza.jpg?la=en\u0026mw=213\u0026modified=20200117215305\u0026hash=396AAC63297CE319876C14B8CF46F4DD06C4605E'
          }
        },
        {
          email: 'etorry@habaneroconsulting.com',
          jobTitle: 'Finance Manager',
          location: 'Vancouver',
          name: 'Elizabeth Torry',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/elizabeth-torry.jpg?la=en\u0026mw=213\u0026modified=20170822212614\u0026hash=9A2655F3992848A78DD1103C199A9B756373D807'
          }
        },
        {
          email: 'hdoyle@habaneroconsulting.com',
          jobTitle: 'Project Manager',
          location: 'Vancouver',
          name: 'Heather Doyle',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/heather-doyle.jpg?la=en\u0026mw=213\u0026modified=20170822212650\u0026hash=FC9F51C9B68BF00A20524DD4FBE208DA4DEF2FAC'
          }
        },
        {
          email: 'ithobani@habaneroconsulting.com',
          jobTitle: 'Managing Consultant, Workplace Analytics',
          location: 'Calgary',
          name: 'Imran Thobani',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/imran-thobani.jpg?la=en\u0026mw=213\u0026modified=20181113192841\u0026hash=0ED879A27E0CA5D9852ED3F3BBC62DF42812CC04'
          }
        },
        {
          email: 'jmckenna@habaneroconsulting.com',
          jobTitle: 'Office Manager',
          location: 'Vancouver',
          name: 'Jarrod McKenna',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/jarrod-mckenna.jpg?la=en\u0026mw=213\u0026modified=20170822222016\u0026hash=D152C4A02E058FA496770BB4675FEC2439D68464'
          }
        },
        {
          email: 'jmarwah@habaneroconsulting.com',
          jobTitle: 'Front-End Developer',
          location: 'Toronto',
          name: 'Jasjit Marwah',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/jasjit-marwah.jpg?la=en\u0026mw=213\u0026modified=20190820190047\u0026hash=562F28534139141992A32670473A857CED3661E1'
          }
        },
        {
          email: 'jecheverry@habaneroconsulting.com',
          jobTitle: 'Product Designer',
          location: 'Toronto',
          name: 'Jorge Echeverry',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/jorge-echeverry.jpg?la=en\u0026mw=213\u0026modified=20190926215528\u0026hash=E5B0D964A34AC937BE7E69D557EF629510632BC3'
          }
        },
        {
          email: 'jchrapavy@habaneroconsulting.com',
          jobTitle: 'Digital Workplace Consultant',
          location: 'Calgary',
          name: 'Julie Chrapavy',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/julie-chrapavy.jpg?la=en\u0026mw=213\u0026modified=20190611174436\u0026hash=0CE70E59B927E2F3C54DC2B82E15223E42BD41E9'
          }
        },
        {
          email: 'kalexander@habaneroconsulting.com',
          jobTitle: 'Solution Developer',
          location: 'Calgary',
          name: 'Kelsey Alexander',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/kelsey-alexander.jpg?la=en\u0026mw=213\u0026modified=20190315224200\u0026hash=91EC377B7305448AB3457DFDD78C389F0C32734F'
          }
        },
        {
          email: 'llau@habaneroconsulting.com',
          jobTitle: 'Chief Architect',
          location: 'Vancouver',
          name: 'Larry Lau',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/larry-lau.jpg?la=en\u0026mw=213\u0026modified=20170822213017\u0026hash=FABE9B7FF3DFFD4996B4BFF9D0468B5722F6C0E9'
          }
        },
        {
          email: 'lsokoluk@habaneroconsulting.com',
          jobTitle: 'Project Manager',
          location: 'Vancouver',
          name: 'Lori Sokoluk',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/lori-sokoluk.jpg?la=en\u0026mw=213\u0026modified=20181204180054\u0026hash=05997F123B93155D07FB864ED81A97EA9BCD38B1'
          }
        },
        {
          email: 'moconnor@habaneroconsulting.com',
          jobTitle: 'Practice Lead, Culture and Transformation',
          location: 'Vancouver',
          name: "Mallory O'Connor",
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/mallory-oconnor.jpg?la=en\u0026mw=213\u0026modified=20171214164148\u0026hash=004253F3E0C1D1851E3BC00D8CE388353087E344'
          }
        },
        {
          email: 'mchapple@habaneroconsulting.com',
          jobTitle: 'Infrastructure Architect',
          location: 'Calgary',
          name: 'Matt Chapple',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/matt-chapple.jpg?la=en\u0026mw=213\u0026modified=20191016171447\u0026hash=917EA9AC418E1B8EF9175763DF0E29EB570AB6D0'
          }
        },
        {
          email: 'myuan@habaneroconsulting.com',
          jobTitle: 'Interaction Designer',
          location: 'Toronto',
          name: 'Michelle Yuan',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/michelle-yuan.jpg?la=en\u0026mw=213\u0026modified=20200922195144\u0026hash=026B62978F0E2580D71D397C068D6508B6517791'
          }
        },
        {
          email: 'mjensen@habaneroconsulting.com',
          jobTitle: 'Solution Architect',
          location: 'Vancouver',
          name: 'Mike Jensen',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/mike-jensen.jpg?la=en\u0026mw=213\u0026modified=20170822230803\u0026hash=A54A2339F0CBABE37F098A8079217D486764597F'
          }
        },
        {
          email: 'mkhowaja@habaneroconsulting.com',
          jobTitle: 'Digital Workplace Consultant',
          location: 'Toronto',
          name: 'Mustansir Khowaja',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/mustansir-khowaja.jpg?la=en\u0026mw=213\u0026modified=20200911170215\u0026hash=FFE75F809C27C9116F8F8D88D7501AF2073E1D88'
          }
        },
        {
          email: 'nveldhoen@habaneroconsulting.com',
          jobTitle: 'Senior Consultant, Enterprise Content Management',
          location: 'Calgary',
          name: 'Natalie Veldhoen',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/natalie-veldhoen.jpg?la=en\u0026mw=213\u0026modified=20170831202806\u0026hash=0DD0D2D05B836D7E5E5CE2CE92BFC81867BE3E96'
          }
        },
        {
          email: 'pgaliana@habaneroconsulting.com',
          jobTitle: 'Director, Content Marketing',
          location: 'Toronto',
          name: 'Pilar Galiana',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/pilar-galiana.jpg?la=en\u0026mw=213\u0026modified=20170822232455\u0026hash=C4F908CF21F7CEB1D9BA1BAA8CFB1065D0884D64'
          }
        },
        {
          email: 'sdeveber@habaneroconsulting.com',
          jobTitle: 'Digital Workplace Consultant',
          location: 'Toronto',
          name: 'Scott deVeber',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/scott-deveber.jpg?la=en\u0026mw=213\u0026modified=20190910011434\u0026hash=097C054AFE99EB77F5CE1914D6A71383E37C31C1'
          }
        },
        {
          email: 'smckee@habaneroconsulting.com',
          jobTitle: 'Project Manager',
          location: 'Calgary',
          name: 'Simona McKee',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/simona-mckee.jpg?la=en\u0026mw=213\u0026modified=20190522161136\u0026hash=A3DE0BB90C0E479E78075C5E208A4274E2F31D91'
          }
        },
        {
          email: 'vip@habaneroconsulting.com',
          jobTitle: 'Product Support Specialist',
          location: 'Vancouver',
          name: 'Vanessa Ip',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/vanessa-ip.jpg?la=en\u0026mw=213\u0026modified=20190320181715\u0026hash=7958EA2C9AD7598B8606962D7E132E563FCB8BE1'
          }
        }
      ]
    },
    {
      title: 'Adventure',
      description:
        'You’re a thrill seeker. Sure-footed and nimble, you love not knowing what’s going to happen next. This year, you found new ways to get off-road and away from the crowds, making your own fun, even under limiting circumstances. In 2021, you can bring your fresh perspective and talent for tricky, courageous questions to complex or longstanding problems. If you do, you’ll get people thinking in new ways that shake up the status quo and inspire real change!',
      slug: 'adventure',
      image: {
        alt: `A drawing of a young person with a tan complexion and brown hair wearing dark grey shirt and pants and an orange backpack. They’re flanked on either side by palm trees and low, green plants. A pink butterfly flutters above their head. At the
        bottom, white text on a black background says, “The Explorer.”`,
        src: '/2020/images/results/explorer.png'
      },
      socialMediaImage: {
        src: '/2020/images/social/the-explorer.png'
      },
      socialMediaTitle: 'Say farewell to 2020 with Habanero’s holiday tarot quiz.',
      socialMediaDescription: 'Find out how to make the most of the new year.',
      habsHeading: 'Check out the other Habs who will be having an adventurous 2021:',
      habs: [
        {
          email: 'bedwards@habaneroconsulting.com',
          jobTitle: 'Director, Products and Services',
          location: 'Vancouver',
          name: 'Brian Edwards',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/brian-edwards.jpg?la=en\u0026mw=213\u0026modified=20170822212127\u0026hash=441EDF85CAB3D073A3957EE28BEE336B5EEA430C'
          }
        },
        {
          email: 'csanders@habaneroconsulting.com',
          jobTitle: 'Senior Advisor, Workplace Experiences',
          location: 'Vancouver',
          name: 'Caterina Sanders',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/caterina-sanders.jpg?la=en\u0026mw=213\u0026modified=20190523190502\u0026hash=E8312E8F8C7ED875DEDFCC7958F0F079A2E2C06E'
          }
        },
        {
          email: 'cscollard@habaneroconsulting.com',
          jobTitle: 'Junior Product Support Specialist',
          location: 'Toronto',
          name: 'Christopher Scollard',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/christopher-scollard.jpg?la=en\u0026mw=213\u0026modified=20200401174855\u0026hash=13CE0DED9F1359CC2351A097FA51E0269BF449C8'
          }
        },
        {
          email: 'egolanowski@habaneroconsulting.com',
          jobTitle: 'Managing Consultant, ServiceNow',
          location: 'Calgary',
          name: 'Elise Golanowski',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/elise-golanowski.jpg?la=en\u0026mw=213\u0026modified=20190123194345\u0026hash=0298DAA2D36812334EA1E617DC94333D9E7D951D'
          }
        },
        {
          email: 'fallan@habaneroconsulting.com',
          jobTitle: 'Digital Workplace Consultant',
          location: 'Vancouver',
          name: 'Fraser Allan',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/fraser-allan.jpg?la=en\u0026mw=213\u0026modified=20191118185358\u0026hash=CA40728D9B5374D6799A90B348A8B6C9457DCE7E'
          }
        },
        {
          email: 'mbice@habaneroconsulting.com',
          jobTitle: 'Solution Architect, Front-End',
          location: 'Vancouver',
          name: 'Mark Bice',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/mark-bice.jpg?la=en\u0026mw=213\u0026modified=20170822224729\u0026hash=F326EAC4F9D1CA501C7833381E198F69F6E98D63'
          }
        },
        {
          email: 'mkurtenbach@habaneroconsulting.com',
          jobTitle: 'Experience Designer',
          location: 'Calgary',
          name: 'Megan Kurtenbach',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/megan-kurtenbach.jpg?la=en\u0026mw=213\u0026modified=20191121215445\u0026hash=85468A40D38F73F93B3F4327DFCF4FDD23F24983'
          }
        },
        {
          email: 'mdumka@habaneroconsulting.com',
          jobTitle: 'Digital Workplace Consultant',
          location: 'Winnipeg',
          name: 'Mike Dumka',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/mike-dumka.jpg?la=en\u0026mw=213\u0026modified=20180920171338\u0026hash=017B24893E3B716285EFBF93D34E500E9243EC82'
          }
        },
        {
          email: 'rchan@habaneroconsulting.com',
          jobTitle: 'Technical Specialist',
          location: 'Toronto',
          name: 'Ruben Chan',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/ruben-chan.jpg?la=en\u0026mw=213\u0026modified=20171207203550\u0026hash=D5A48E04E3C4C0FAB3FDE42467706F10FF83691B'
          }
        },
        {
          email: 'rbender@habaneroconsulting.com',
          jobTitle: 'Digital Workplace Consultant',
          location: 'Calgary',
          name: 'Ryan Bender',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/ryan-bender.jpg?la=en\u0026mw=213\u0026modified=20170822233302\u0026hash=8A8D701EEDF112E8B807E6496A66EB1950C606C0'
          }
        },
        {
          email: 'sbrown@habaneroconsulting.com',
          jobTitle: 'Quality Assurance Specialist',
          location: 'Vancouver',
          name: 'Stephen Brown',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/stephen-brown.jpg?la=en\u0026mw=213\u0026modified=20170822234415\u0026hash=0FF74F62CD7E7A125F0A27EE847FB93BC57B76AF'
          }
        },
        {
          email: 'sbrown@habaneroconsulting.com',
          jobTitle: 'Quality Assurance Specialist',
          location: 'Vancouver',
          name: 'Stephen Brown',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/stephen-brown.jpg?la=en\u0026mw=213\u0026modified=20170822234415\u0026hash=0FF74F62CD7E7A125F0A27EE847FB93BC57B76AF'
          }
        },
        {
          name: 'Terry Chow',
          link: 'https://www.habaneroconsulting.com/team',
          image: {
            src:'images/habs/terry-chow.jpg'
          }
        },
        {
          email: 'taddison@habaneroconsulting.com',
          jobTitle: 'Project Manager',
          location: 'Calgary',
          name: 'Ty Addison',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/ty-addison.jpg?la=en\u0026mw=213\u0026modified=20190315224310\u0026hash=D20FCCE675670AA2FF21694F8E0155F19258971A'
          }
        },
        {
          email: 'whardy@habaneroconsulting.com',
          jobTitle: 'Practice Lead, Employee Portals',
          location: 'Vancouver',
          name: 'Will Hardy',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/william-hardy.jpg?la=en\u0026mw=213\u0026modified=20170822155058\u0026hash=42E30E3717B552BB4DA87EACF3920E3202901C06'
          }
        }
      ]
    },
    {
      title: 'Togetherness',
      description:
        'You’re a true people-person. You’re six degrees from everyone and the last to leave the Zoom party. Curious and friendly, you make everyone feel heard and understood. This year, you found creative ways to stay connected with your social circles through ever-changing conditions. In 2021, you can use your ninja-level social skills to help others find co-conspirators for their passion projects. Your superpower is bringing groups of people together to make great things happen – now’s your time to shine!',
      slug: 'togetherness',
      image: {
        alt: `A drawing of a grey-haired person with a rosy complexion wearing a blue shirt and standing with their arms raised as they talk and gesture. Above their head is a screen divided in four squares of different colours: green, blue, orange and pink.
        In each of three of the squares is a different person; in the fourth square is a dog. They are all smiling and chatting with each other, as on a video call. Near the bottom of the drawing are four icons that look like buttons: an envelope for email, a heart
        for liking, a speech bubble for chatting and a phone for calling. Below, white text on a black background says, “The Connector.”`,
        src: '/2020/images/results/connector.png'
      },
      socialMediaImage: {
        src: '/2020/images/social/the-connector.png'
      },
      socialMediaTitle: 'Say farewell to 2020 with Habanero’s holiday tarot quiz.',
      socialMediaDescription: 'Find out how to make the most of the new year.',
      habsHeading: 'Check out the other Habs who will be having a connected 2021:',
      habs: [
        {
          email: 'cbronsch@habaneroconsulting.com',
          jobTitle: 'Interaction Designer',
          location: 'Calgary',
          name: 'Carmen Bronsch',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/carmen-bronsch.jpg?la=en\u0026mw=213\u0026modified=20191016175342\u0026hash=B22A6C7522D4E7866D4FA88DB9BA1A8D9FB82738'
          }
        },
        {
          email: 'shagarty@habaneroconsulting.com',
          jobTitle: 'Digital Marketing Coordinator',
          location: 'Toronto',
          name: 'Sarah Hagarty',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/sarah-hagarty.jpg?la=en\u0026mw=213\u0026modified=20200623163355\u0026hash=824DD3D6B9B03B43E384377601F284F9A14F8B0B'
          }
        },
        {
          email: 'aschafer@habaneroconsulting.com',
          jobTitle: 'Employee Experience Coordinator',
          location: 'Toronto',
          name: 'Ashleigh Schafer',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/ashleigh-schafer.jpg?la=en\u0026mw=213\u0026modified=20190926211546\u0026hash=7B6CB35735803BC3498EA4A97F7D0D8718562D60'
          }
        },
        {
          email: 'lnugent@habaneroconsulting.com',
          jobTitle: 'Experience Designer',
          location: 'Vancouver',
          name: 'Laura Nugent',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/laura-nugent.jpg?la=en\u0026mw=213\u0026modified=20190709180955\u0026hash=86F8BA0D647EC4951DD3765A667979102BC7F27D'
          }
        }
      ]
    },
    {
      title: 'Ambition',
      description:
        'You’re an achiever. Setting and reaching your goals gives you a natural high. While others are lounging in their pjs, you’re crushing another rep, checking your portfolio or levelling up your second-language skills. This year, you learned to live without a finish line by making the most of your current situation – whatever it happened to be. In 2021, you can use your innate ability to lean into the levers for change that open up new opportunities for you and the people you care about.',
      slug: 'ambition',
      image: {
        alt: `A drawing of a grey-hair person with a peachy complexion wearing a grey dress shirt, dark grey pants and shoes and a bright pink tie. He’s sitting on top of a skyscraper, surrounded by other tall buildings. He’s resting his elbow resting on
        one of the other tall buildings around him, and a takeout coffee cup sits on top of another building. He looks excited to be talking on his cellphone. At the bottom, white text on a black background says, “The Mogul.”`,
        src: '/2020/images/results/mogul.png'
      },
      socialMediaImage: {
        src: '/2020/images/social/the-mogul.png'
      },
      socialMediaTitle: 'Say farewell to 2020 with Habanero’s holiday tarot quiz.',
      socialMediaDescription: 'Find out how to make the most of the new year.',
      habsHeading: 'Check out the other Habs who will be having an ambitious 2021:',
      habs: [
        {
          email: 'benskelton@habaneroconsulting.com',
          jobTitle: 'Vice President',
          location: 'Toronto',
          name: 'Ben Skelton',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/ben-skelton.jpg?la=en\u0026mw=213\u0026modified=20170822224339\u0026hash=9879EF86247614B4E1F388E23084BC09DF95A879'
          }
        },
        {
          email: 'kaujla@habaneroconsulting.com',
          jobTitle: 'Director, Operations',
          location: 'Vancouver',
          name: 'Kiran Aujla',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/kiran-aujla.jpg?la=en\u0026mw=213\u0026modified=20170822212833\u0026hash=3E8ADC68FB0F1B2BCC27B8F8D895EFF9A024A934'
          }
        },
        {
          email: 'mwerake@habaneroconsulting.com',
          jobTitle: 'Quality Assurance Consultant',
          location: 'Vancouver',
          name: 'Madhava Werake',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/madhava-werake.jpg?la=en\u0026mw=213\u0026modified=20180711164009\u0026hash=E09CEB0F1ABDB50948EACC08450EB9795D481F86'
          }
        }
      ]
    },
    {
      title: 'Inspiration',
      description:
        'You’re a creative force. It’s not unusual for you to always have a million projects on the go. This year, you may have tried your hand at new hobbies or just enjoyed digging deeper into your own esoteric interests in the garden, kitchen or at the workbench. In 2021, you can share your gifts with a larger audience than ever before. Your passion and playfulness inspire others to explore the things that motivate them too, so don’t hold back!',
      slug: 'inspiration',
      image: {
        alt: `A drawing of a dark-haired person with a dark complexion, standing against a vibrant pink background. They’re dressed in a white shirt, grey pants and a blue apron that is spattered with paint. In one hand, they’re holding a guitar. Their other
        arm is raised in the air, holding a paint palette with red, yellow and blue paint on it. Musical notes and drawing of a cat and a tulip float above their head. On the ground there is a toolbox containing a pencil, ruler, and hammer. At the bottom, white text
        on a black background says, “The Maker.”`,
        src: '/2020/images/results/maker.png'
      },
      socialMediaImage: {
        src: '/2020/images/social/the-maker.png'
      },
      socialMediaTitle: 'Say farewell to 2020 with Habanero’s holiday tarot quiz.',
      socialMediaDescription: 'Find out how to make the most of the new year.',
      habsHeading: 'Check out the other Habs who will be having an inspired 2021:',
      habs: [
        {
          email: 'sbraun@habaneroconsulting.com',
          jobTitle: 'Writer and Content Consultant',
          location: 'Toronto',
          name: 'Sandra Braun',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/sandra-braun.jpg?la=en\u0026mw=213\u0026modified=20180814224047\u0026hash=9114F6DCB9FB56772762414D56385BA86CF391C7'
          }
        },
        {
          email: 'kbeard@habaneroconsulting.com',
          jobTitle: 'Creative Director',
          location: 'Vancouver',
          name: 'Kurtis Beard',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/kurtis-beard.jpg?la=en\u0026mw=213\u0026modified=20170822212911\u0026hash=79F1797D625FECFAB488D9593A80745DD501E538'
          }
        },
        {
          email: 'ccowen@habaneroconsulting.com',
          jobTitle: 'Interaction Designer',
          location: 'Toronto',
          name: 'Christina Cowen',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/christina-cowen.jpg?la=en\u0026mw=213\u0026modified=20200117210543\u0026hash=4C11E29D8863B43F7014057BD285AD9E753154FA'
          }
        }
      ]
    },
    {
      title: 'Mindfulness',
      description:
        'You are an idealist. Your calm and kind nature puts people at ease and brings out the best in every situation. This year, you learned to make mindful choices about who you engage with, how you set up your environment and the activities you spend your time and energy on every day. In 2021, you can expand your circle of awareness. Seek out and join with others who share your sense of purpose, so you can support each other to shine your light in the world.',
      slug: 'mindfulness',
      image: {
        alt: `A drawing of a bald person with a dark complexion, sitting cross-legged in a meditative pose with their arms extended outwards and their hands in a mudra. They have a peaceful smile on their face and their eyes are closed. They are floating
        above the serene blue floor; the background is light grey. On either side of them are small plants with light green leaves. Pink and white stars float in the air around their head. At the bottom, white text on a black background says, “The Zen Master.”`,
        src: '/2020/images/results/zen-master.png'
      },
      socialMediaImage: {
        src: '/2020/images/social/the-zen-master.png'
      },
      socialMediaTitle: 'Say farewell to 2020 with Habanero’s holiday tarot quiz.',
      socialMediaDescription: 'Find out how to make the most of the new year.',
      habsHeading: 'Check out the other Habs who will be having a mindful 2021:',
      habs: [
        {
          email: 'erosu@habaneroconsulting.com',
          jobTitle: 'Senior Solution Developer',
          location: 'Vancouver',
          name: 'Elena Rosu',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/elena-rosu.jpg?la=en\u0026mw=213\u0026modified=20170822214522\u0026hash=046B64476ACE2BE1F4427A05B4881CC4C7310CAF'
          }
        },
        {
          email: 'jsloane@habaneroconsulting.com',
          jobTitle: 'Director, Employee Experience',
          location: 'Calgary',
          name: 'James Sloane',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/james-sloane.jpg?la=en\u0026mw=213\u0026modified=20170822212723\u0026hash=0969CF312EB7FC7DECC1DB68C58738D09071AC1F'
          }
        },
        {
          email: 'mvdbosch@habaneroconsulting.com',
          jobTitle: 'Information Technology  Manager',
          location: 'Vancouver',
          name: 'Marty van den Bosch',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/marty-vandenbosch.jpg?la=en\u0026mw=213\u0026modified=20170822225109\u0026hash=51D94ECFA04B9E0ECE1BCAE1D4183514A709AB56'
          }
        },
        {
          email: 'mjarmstrong@habaneroconsulting.com',
          jobTitle: 'Senior Solution Developer',
          location: 'Calgary',
          name: 'Michael Armstrong',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/michael-armstrong.jpg?la=en\u0026mw=213\u0026modified=20170822230505\u0026hash=6B0645F5DAD682526393CFDAB4E3DB05855C31C0'
          }
        },
        {
          email: 'nioffe@habaneroconsulting.com',
          jobTitle: 'Front-End Developer',
          location: 'Toronto',
          name: 'Nataliya Ioffe',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/nataliya-ioffe.jpg?la=en\u0026mw=213\u0026modified=20190523180140\u0026hash=B48E1A4D16E2625A594EDC491079C5C753C19335'
          }
        },
        {
          name: 'Ryan Taylor',
          link: 'https://www.habaneroconsulting.com/team',
          image: {
            src:'images/habs/ryan-taylor.jpg'
          }
        },
        {
          email: 'sfitzgerald@habaneroconsulting.com',
          jobTitle: 'President',
          location: 'Vancouver',
          name: 'Steven Fitzgerald',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/steven-fitzgerald.jpg?la=en\u0026mw=213\u0026modified=20180226212953\u0026hash=66C0F34EA5823525BB877D7717B910907B9B5945'
          }
        },

        {
          email: 'talbert@habaneroconsulting.com',
          jobTitle: 'Project Manager',
          location: 'Vancouver',
          name: 'Toni Albert',
          image: {
            src:
              'https://cdn.habaneroconsulting.com/-/media/project/habanero/content/team/toni-albert.jpg?la=en\u0026mw=213\u0026modified=20171004193801\u0026hash=27360BED61C1B5818CB01C1F04698AD482349BFD'
          }
        }
      ]
    }
  ]
};
